import { useMemo } from 'react';
import {
  AspectRatio,
  Divider,
  HStack,
  Img,
  Text,
  VStack,
} from '@chakra-ui/react';
import { differenceInMonths } from 'date-fns';

import { Button, Link } from 'components';
import {
  formatDateToApp,
  getDomainFromUrl,
  parseDateFromServer,
} from 'utils/helpers';
import { usePageTextContent } from 'context/pageTextContent';

interface CheckoutButton {
  text: string;
  isGrey: boolean;
}

interface Props {
  network: AdaptedNetworkWithOutlets;
}

export const NetworkProfile = (props: Props) => {
  const { network } = props;

  const { content } = usePageTextContent<NetworkProfilePage>();

  const {
    stores,
    checkoutDescription,
    checkoutButton,
    salesStoppedButton,
    startsOnButton,
  } = content;

  const {
    giftcardFrontpage,
    legalEntityName,
    address,
    portal: { onlineCheckoutEnabled, enableOnlineCheckoutAt },
    networkType,
  } = network;

  const networkUrl = String(network.fqdn || network.slugFqdn);
  const networkUrlLabel = getDomainFromUrl(networkUrl);
  const isNetworkAssociation = networkType === 'ASSOCIATION';

  const showNetworkCheckoutLink =
    onlineCheckoutEnabled && !isNetworkAssociation;
  const showDisabledCheckoutButton =
    !onlineCheckoutEnabled && !isNetworkAssociation;

  const showCheckoutDescription =
    showNetworkCheckoutLink || showDisabledCheckoutButton;

  const showAddress = !legalEntityName.toLowerCase().startsWith('zmyle');

  const { text, isGrey } = useMemo<CheckoutButton>(() => {
    if (onlineCheckoutEnabled) {
      return {
        text: checkoutButton,
        isGrey: false,
      };
    }

    const parsedDate = parseDateFromServer(enableOnlineCheckoutAt, {
      timezoneId: network.region.timezoneId,
    });

    const monthsDifference = differenceInMonths(
      parsedDate || new Date(),
      new Date(),
    );

    if (monthsDifference >= 6) {
      return {
        text: salesStoppedButton,
        isGrey: true,
      };
    }

    return {
      text: `${startsOnButton} ${formatDateToApp(parsedDate)}`,
      isGrey: false,
    };
  }, [network]);

  return (
    <VStack
      px="0.75rem"
      py="1rem"
      bg="white"
      rounded="lg"
      shadow="2xl"
      spacing="1rem"
      w="full"
      maxW={['32.25rem', '32.25rem', '20.3rem']}
      align="flex-start"
      fontWeight="light"
    >
      <VStack w="full" align="flex-start" spacing="0.5rem">
        <AspectRatio ratio={3 / 2} w="full" minW="18.75rem" mt="-3rem">
          <Img
            src={giftcardFrontpage.url}
            rounded="lg"
            shadow="xl"
            loading="lazy"
            alt={network.name}
          />
        </AspectRatio>

        <Text fontSize="1.125rem" fontWeight="normal" px="0.5rem">
          {network.name}
        </Text>

        <HStack w="full" justify="space-between" px="0.5rem">
          <Text>{stores}</Text>

          <Text color="store.primary">{network.outlets.length}</Text>
        </HStack>

        <Link
          href={networkUrl}
          isExternal
          target="_blank"
          color="network.primary"
          _hover={{ textDecoration: 'underline', color: 'network.primary' }}
          px="0.5rem"
        >
          {networkUrlLabel}
        </Link>
      </VStack>

      <Divider />

      {showAddress && (
        <VStack w="full" align="flex-start" spacing="0.5rem" px="0.5rem">
          <Text>{legalEntityName}</Text>

          <Text>{address.streetAddress}</Text>

          <Text>{`${address.zipcode} ${address.city}`}</Text>

          {showCheckoutDescription && <Divider />}
        </VStack>
      )}

      {showCheckoutDescription && (
        <Text fontSize="0.75rem" fontWeight="normal" px="0.5rem">
          {checkoutDescription}
        </Text>
      )}

      {showNetworkCheckoutLink && (
        <Link
          href={`/checkout/${network.slug}`}
          target="_self"
          w="full"
          noHoverEffect
          isExternal
        >
          <Button
            variant="network"
            w="full"
            bg={isGrey ? 'gray.500' : undefined}
          >
            {text}
          </Button>
        </Link>
      )}

      {showDisabledCheckoutButton && (
        <Button
          variant="network"
          w="full"
          bg={isGrey ? 'gray.500' : undefined}
          isDisabled
        >
          {text}
        </Button>
      )}
    </VStack>
  );
};
